/**
 * Question World Module - MCQInput Component
 * Multiple Choice Question input handler
 */

import { Radio as AntRadio, Col, Image, Row, Typography } from 'antd';
import { Radio } from 'antd-mobile';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import toastr from 'toastr';

import { MathInput } from '../common';
import RadioOptionLabel from './RadioOptionLabel';

import {
    setAnsweredQuestionCount,
    setOfflineSavedAnswers,
    setNoOfSubmittedResponse
} from '../../../../../../stateManager/reducers/studentAuthSlice';

import { compareArray, shouldUseKatex, radioLabels, bucket_url, bucket_url_old } from '../../helpers';
import { Answer } from '../../helpers/Answer';

const { Text } = Typography;
const RadioGroup = AntRadio.Group;

let answer = null;

const MCQInput = props => {
    const {
        isResult,
        question,
        examId,
        groupId,
        examMeta,
        isPracticeQuiz = false,
        onAnswerSelect,
        showAnswer = false
    } = props;
    const dispatch = useDispatch();
    const options = isResult ? question?.questionId?.options : question?.options;
    const optionType = isResult
        ? question?.questionId?.optionType &&
            typeof question?.questionId?.optionType === 'string'
            ? JSON.parse(question?.questionId?.optionType)
            : undefined
        : question?.optionType && typeof question?.optionType === 'string'
            ? JSON.parse(question?.optionType)
            : undefined;

    const [selectedOption, setSelectedOption] = useState();
    const [isSubmittingAnswer] = useState(false);

    useEffect(() => {
        const localData = localStorage.getItem('localSavedAnswers');
        if (localData) {
            const localSavedAnswers = JSON.parse(localData);
            if (localSavedAnswers[examId]) {
                Object.keys(localSavedAnswers[examId]).forEach(qid => {
                    const q = localSavedAnswers[examId][qid];
                    if (qid) {
                        dispatch(setAnsweredQuestionCount({ id: qid, flag: true }));
                    }
                    if (q.offline === true) {
                        dispatch(setOfflineSavedAnswers(qid));
                    }
                });
                if (localSavedAnswers[examId]?.[question._id]) {
                    const ans = localSavedAnswers[examId]?.[question._id]?.answer?.answer;
                    setSelectedOption(ans);
                }
            }
        }
    }, [question, dispatch, examId]);

    const saveToLocalStorage = ({ offline, answer: ans, timestamp }) => {
        if (offline) dispatch(setOfflineSavedAnswers(question._id));
        const getLocalData = localStorage.getItem('localSavedAnswers');
        let isPrevious = false;
        if (getLocalData) {
            const localSavedAnswers = JSON.parse(getLocalData);
            isPrevious = !!localSavedAnswers[examId];
        }
        if (isPrevious) {
            const localSavedAnswers = JSON.parse(getLocalData);
            localSavedAnswers[examId][question._id] = {
                answer: { text: [ans], answer: ans },
                offline,
                timestamp
            };
            localStorage.setItem('localSavedAnswers', JSON.stringify(localSavedAnswers));
        } else {
            const localSavedAnswers = {
                [examId]: {
                    [question._id]: {
                        answer: { text: [ans], answer: ans },
                        offline,
                        timestamp
                    }
                }
            };
            localStorage.setItem('localSavedAnswers', JSON.stringify(localSavedAnswers));
        }
    };

    const handleAnswerSubmit = async ({ value, timestamp }) => {
        if (isPracticeQuiz || examMeta?.isInstantPractice) {
            saveToLocalStorage({ answer: value, offline: false, timestamp });
            if (typeof onAnswerSelect === 'function') {
                onAnswerSelect([value]);
            }
            return;
        }

        saveToLocalStorage({ answer: value, offline: false, timestamp });
        const data = {
            questionId: question._id,
            answer: [value],
            timestamp
        };

        try {
            if (answer) {
                answer.cancel();
            }
            answer = new Answer(dispatch, groupId, examId, data, question, false, () => setSelectedOption(undefined), !examMeta?.examId?.isPracticeExam);
            const response = await answer.handleSubmit();
            if (response?.response?.status === '200') {
                dispatch(setNoOfSubmittedResponse({
                    id: question._id,
                    count: 1
                }));
            }
        } catch (err) {
            console.log('ANSWER => answer submit err: ', err);
            saveToLocalStorage({ answer: value, offline: true, timestamp });
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const deboucedSubmit = useCallback(
        debounce(({ value, timestamp }) => {
            handleAnswerSubmit({ value, timestamp });
        }, 250),
        []
    );

    const shouldShowResult =
        isResult || (isPracticeQuiz && question?.answer && question?.answer.length > 0) || showAnswer;

    if (!shouldShowResult) {
        return (
            <Col xs={23} md={23} className='mcq-input-container' offset={1}>
                {options.map((option, index) => {
                    let ansClasss = '';
                    return (
                        <Radio.RadioItem
                            key={`option-${index}`}
                            checked={option === selectedOption}
                            className={
                                ' custom-radio list_item_option option-' + (index + 1) + ' ' + ansClasss
                            }
                            disabled={true}
                        >
                            <div
                                className='custom-radio-prepend'
                                onClick={e => {
                                    if (
                                        isSubmittingAnswer ||
                                        (selectedOption &&
                                            examMeta?.multipleTimesSubmission === false)
                                    ) {
                                        toastr.error('You cannot change the answer');
                                        return;
                                    }
                                    setSelectedOption(option);
                                    deboucedSubmit({ value: option, timestamp: Date.now() });
                                }}
                            >
                                <span
                                    className={
                                        'custom-radio-icon' +
                                        (option === selectedOption ? ' active' : ' ')
                                    }
                                >
                                    {radioLabels[index]}
                                </span>
                            </div>
                            <span>
                                {optionType &&
                                    optionType[option] &&
                                    optionType[option] === 'image' ? (
                                    <Image
                                        placeholder
                                        src={(option?.startsWith('user') ? bucket_url_old : bucket_url) + option}
                                        width={'100%'}
                                        style={{ objectFit: 'contain' }}
                                    />
                                ) : (
                                    <Text className='input-options'>
                                        {shouldUseKatex(option) >= 0 ? (
                                            <MathInput mathItem={option} />
                                        ) : (
                                            option
                                        )}
                                    </Text>
                                )}
                            </span>
                        </Radio.RadioItem>
                    );
                })}
            </Col>
        );
    }

    return (
        <Col xs={23} md={23} offset={1}>
            <RadioGroup
                style={{ width: '100%' }}
                value={
                    shouldShowResult && question?.answer && question?.answer.length > 0
                        ? question?.answer[0]
                        : (showAnswer && question?.questionId?.answer?.[0]
                            ? question?.questionId?.answer[0]
                            : selectedOption)
                }
                onChange={e => {
                    setSelectedOption(e.target.value);
                    deboucedSubmit({ value: e.target.value, timestamp: Date.now() });
                }}
                disabled={
                    isSubmittingAnswer ||
                    showAnswer ||
                    (selectedOption && examMeta?.multipleTimesSubmission === false)
                }
            >
                <Row align='middle' className='list_item_option_container'>
                    {options.map((option, index) => {
                        let ansClasss = '';
                        const correctAnswer = isResult
                            ? question?.questionId?.answer?.[0]
                            : (question?.questionId?.answer?.[0] || question?.answer?.[0]);
                        const userAnswer = question?.answer?.[0] || selectedOption;

                        if (shouldShowResult && correctAnswer) {
                            if (compareArray(Array(correctAnswer), Array(option))) {
                                if (userAnswer && compareArray(Array(userAnswer), Array(option))) {
                                    ansClasss += ' correct ';
                                } else {
                                    ansClasss += ' correctbutnotans ';
                                }
                            }
                            if (userAnswer && !compareArray(Array(correctAnswer), Array(option)) && compareArray(Array(userAnswer), Array(option))) {
                                ansClasss += ' incorrect ';
                            }
                        }
                        return (
                            <RadioOptionLabel
                                key={`option-${index}`}
                                option={option}
                                optionType={optionType}
                                isResult={isResult}
                                index={index}
                                ansClasss={ansClasss}
                                multipleTimesSubmission={examMeta?.multipleTimesSubmission}
                            />
                        );
                    })}
                </Row>
            </RadioGroup>
        </Col>
    );
};

export default MCQInput;

